import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1000));

    toast.success("Message sent! We'll get back to you soon.");
    setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-paris-stone overflow-x-hidden" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-midnight-navy pt-24 pb-12 md:pt-28 md:pb-16">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
              Contact Us
            </h1>
            <p className="text-base md:text-lg text-paris-stone/80 max-w-2xl mx-auto">
              We're here to help 24/7. Get in touch with our team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-10 md:py-16 bg-paris-stone">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-soft p-5 md:p-6"
              >
                <h2 className="font-heading font-bold text-lg md:text-xl text-slate-900 mb-5 md:mb-6">
                  Get In Touch
                </h2>

                <div className="space-y-2">
                  <a
                    href="tel:+33123456789"
                    className="flex items-start gap-3 md:gap-4 p-3 md:p-4 rounded-xl hover:bg-slate-50 transition-colors group"
                  >
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-eiffel-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 md:w-5 md:h-5 text-eiffel-blue" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 group-hover:text-eiffel-blue text-sm md:text-base">
                        Phone
                      </p>
                      <p className="text-slate-600 text-sm">+33 763 519 524</p>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/33763519524"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 md:gap-4 p-3 md:p-4 rounded-xl hover:bg-slate-50 transition-colors group"
                  >
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 group-hover:text-green-600 text-sm md:text-base">
                        WhatsApp
                      </p>
                      <p className="text-slate-600 text-sm">+33 763 519 524</p>
                    </div>
                  </a>

                  <a
                    href="mailto:support@bestdisneytransfer.com"
                    className="flex items-start gap-3 md:gap-4 p-3 md:p-4 rounded-xl hover:bg-slate-50 transition-colors group"
                  >
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-eiffel-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 md:w-5 md:h-5 text-eiffel-blue" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 group-hover:text-eiffel-blue text-sm md:text-base">
                        Email
                      </p>
                      <p className="text-slate-600 text-sm break-all">info@bestdisneytransfer.com</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-3 md:gap-4 p-3 md:p-4">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-eiffel-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 md:w-5 md:h-5 text-eiffel-blue" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm md:text-base">Location</p>
                      <p className="text-slate-600 text-sm">11 Square De L'avre,
                        92100 Boulogne-Billancourt,
                      France</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 md:gap-4 p-3 md:p-4">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-champagne-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 md:w-5 md:h-5 text-champagne-gold" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm md:text-base">Hours</p>
                      <p className="text-slate-600 text-sm">24/7 Support</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2 bg-white rounded-2xl shadow-soft p-5 md:p-8"
            >
              <h2 className="font-heading font-bold text-lg md:text-xl text-slate-900 mb-5 md:mb-6">
                Send Us a Message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 block">
                      Full Name *
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Smith"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 block">
                      Email Address *
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 block">
                      Phone Number
                    </Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 block">
                      Subject *
                    </Label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Booking inquiry"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 block">
                    Message *
                  </Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help you?"
                    className="min-h-[120px] md:min-h-[150px]"
                    required
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto bg-midnight-navy hover:bg-midnight-navy/90 rounded-full px-6 md:px-8 py-3"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
